import { useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listMcpPeople, revokeAllPersonMcpApiKeys, revokePersonMcpApiKey } from '@/apps/apis/mcpAccess';
import type { McpApiKeySummary, McpPersonAccess } from '@/apps/apis/mcpAccess/types';
import type { SharedEmailFilter } from './sharedEmailFilter';
import { errorMessage, formatWhen } from './mcpAccessFormat';

const PEOPLE_QUERY_KEY = ['mcpAccess', 'people'] as const;

/** One table row: a person's active key, or the person alone when they have none. */
interface KeyRow {
  person: McpPersonAccess;
  key: McpApiKeySummary | null;
}

type SortColumn = 'email' | 'account' | 'groups' | 'keyLabel' | 'keyId' | 'issued' | 'lastUsed';

const COLUMNS: Array<{ id: SortColumn; title: string; value: (row: KeyRow) => string }> = [
  { id: 'email', title: 'Email', value: (row) => row.person.email.toLowerCase() },
  { id: 'account', title: 'Account label', value: (row) => (row.person.label ?? '').toLowerCase() },
  { id: 'groups', title: 'Groups', value: (row) => row.person.groups.join(', ') },
  { id: 'keyLabel', title: 'Key label', value: (row) => (row.key?.label ?? '').toLowerCase() },
  { id: 'keyId', title: 'Key id', value: (row) => row.key?.keyId ?? '' },
  // ISO timestamps sort as text; a row without a key or a use sorts before every date.
  { id: 'issued', title: 'Issued', value: (row) => row.key?.issuedAt ?? '' },
  { id: 'lastUsed', title: 'Last used', value: (row) => row.key?.lastUsedAt ?? '' },
];

/** The revoke waiting for a confirmation: one key of a person, or all of them. */
interface PendingRevoke {
  email: string;
  keyId: string | null;
}

const toRows = (people: McpPersonAccess[]): KeyRow[] =>
  people.flatMap((person) =>
    person.keys.length ? person.keys.map((key): KeyRow => ({ person, key })) : [{ person, key: null }],
  );

interface McpPeopleContentProps extends SharedEmailFilter {
  /** The teams a team admin manages; null for HR and admins, who see everyone. The Facade already limits the list. */
  managedTeams: string[] | null;
}

/** Other people's MCP keys: sortable by any column, filtered by email, revocable. */
const McpPeopleContent = ({ managedTeams, filter: sharedFilter, onFilterChange }: McpPeopleContentProps) => {
  const queryClient = useQueryClient();
  const people = useQuery({ queryKey: PEOPLE_QUERY_KEY, queryFn: listMcpPeople });
  const [ownFilter, setOwnFilter] = useState('');
  const filter = sharedFilter ?? ownFilter;
  const setFilter = onFilterChange ?? setOwnFilter;
  const [sort, setSort] = useState<{ column: SortColumn; descending: boolean }>({ column: 'email', descending: false });
  const [pending, setPending] = useState<PendingRevoke | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const term = filter.trim().toLowerCase();
  const shownPeople = useMemo(
    () => (people.data ?? []).filter((person) => person.email.toLowerCase().includes(term)),
    [people.data, term],
  );
  const rows = useMemo(() => {
    const value = COLUMNS.find((column) => column.id === sort.column)?.value ?? COLUMNS[0]!.value;
    const sorted = [...toRows(shownPeople)].sort((left, right) => value(left).localeCompare(value(right)));
    return sort.descending ? sorted.reverse() : sorted;
  }, [shownPeople, sort]);

  // Revoking everything of one person is offered only once the filter leaves one person with keys; the button names them.
  const peopleWithKeys = shownPeople.filter((person) => person.keys.length > 0);
  const onlyPerson = term && peopleWithKeys.length === 1 ? peopleWithKeys[0] : undefined;

  const sortBy = (column: SortColumn) =>
    setSort((current) => ({ column, descending: current.column === column ? !current.descending : false }));

  const confirmRevoke = async (revoke: PendingRevoke) => {
    setPending(null);
    setBusy(true);
    setNotice(null);
    setRevokeError(null);
    try {
      if (revoke.keyId) {
        await revokePersonMcpApiKey(revoke.email, revoke.keyId);
        setNotice(`Revoked key ${revoke.keyId} of ${revoke.email}.`);
      } else {
        const { revokedCount } = await revokeAllPersonMcpApiKeys(revoke.email);
        setNotice(`Revoked ${revokedCount} ${revokedCount === 1 ? 'key' : 'keys'} of ${revoke.email}.`);
      }
    } catch (error) {
      setRevokeError(errorMessage(error, 'Could not revoke the key.'));
    } finally {
      setBusy(false);
      // Show what is left, whether the revoke went through or someone else changed the keys meanwhile.
      void queryClient.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY });
    }
  };

  const confirmation = (revoke: PendingRevoke) => (
    <span className="d-inline-flex gap-2">
      <Button size="sm" variant="danger" disabled={busy} onClick={() => void confirmRevoke(revoke)}>
        {revoke.keyId ? 'Confirm revoke' : 'Confirm revoke all'}
      </Button>
      <Button size="sm" variant="outline-secondary" onClick={() => setPending(null)}>
        Cancel
      </Button>
    </span>
  );

  if (people.isPending) {
    return <Spinner animation="border" size="sm" />;
  }
  if (people.isError) {
    return <Alert variant="danger">{errorMessage(people.error, 'Could not load MCP users.')}</Alert>;
  }

  const activeKeys = shownPeople.reduce((count, person) => count + person.keys.length, 0);

  return (
    <>
      <h2 className="h4 mb-1">{managedTeams ? "Your team's MCP keys" : "Everyone's MCP keys"}</h2>
      <p className="text-muted">
        {managedTeams
          ? `Teams you manage: ${managedTeams.join(', ')}. Everyone holding a role in these teams, with their active keys.`
          : 'Every MCP user, agents included, with their active keys.'}{' '}
        Revoke a key someone should no longer have; every revoke is recorded with your email.
      </p>

      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <Form.Control
          aria-label="Filter by email"
          placeholder="Filter by email"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          style={{ maxWidth: 320 }}
        />
        <span className="text-muted small">
          {shownPeople.length} people · {activeKeys} active keys
        </span>
        {onlyPerson && (
          <span className="ms-auto">
            {pending?.email === onlyPerson.email && pending.keyId === null ? (
              confirmation({ email: onlyPerson.email, keyId: null })
            ) : (
              <Button
                size="sm"
                variant="outline-danger"
                disabled={busy}
                onClick={() => setPending({ email: onlyPerson.email, keyId: null })}
              >
                Revoke all keys of {onlyPerson.email}
              </Button>
            )}
          </span>
        )}
      </div>

      {revokeError && <Alert variant="danger">{revokeError}</Alert>}
      {notice && <Alert variant="success">{notice}</Alert>}

      {rows.length === 0 ? (
        <Alert variant="secondary">{term ? `No MCP users match "${filter.trim()}".` : 'No MCP users yet.'}</Alert>
      ) : (
        <Table size="sm" hover responsive>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.id}
                  aria-sort={sort.column === column.id ? (sort.descending ? 'descending' : 'ascending') : 'none'}
                >
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-reset text-decoration-none fw-semibold"
                    onClick={() => sortBy(column.id)}
                  >
                    {column.title}
                    {sort.column === column.id ? (sort.descending ? ' ▼' : ' ▲') : ''}
                  </Button>
                </th>
              ))}
              <th>
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ person, key }) => (
              <tr key={`${person.email}:${key?.keyId ?? 'none'}`}>
                <td>{person.email}</td>
                <td>{person.label ?? ''}</td>
                <td className="small">{person.groups.join(', ')}</td>
                {key ? (
                  <>
                    <td>{key.label ?? '(no label)'}</td>
                    <td>
                      <code>{key.keyId}</code>
                    </td>
                    <td>{formatWhen(key.issuedAt)}</td>
                    <td>{formatWhen(key.lastUsedAt)}</td>
                    <td className="text-end text-nowrap">
                      {pending?.email === person.email && pending.keyId === key.keyId ? (
                        confirmation({ email: person.email, keyId: key.keyId })
                      ) : (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={busy}
                          onClick={() => setPending({ email: person.email, keyId: key.keyId })}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </>
                ) : (
                  <td colSpan={5} className="text-muted">
                    No active keys
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default McpPeopleContent;
